'use client';

import { useCallback } from 'react';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { MagicAuthLoginOptions } from 'src/utils/magicAuthAdapter';
import createMagicWalletAdapter from 'src/utils/magicAuthAdapter';
import { AppKitAdapter } from 'src/utils/AppKitAdapter';
import createPrivyNativeWalletAdapter from 'src/utils/PrivyNativeAdapter';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import {
	DriftCustomWalletName,
	DRIFT_CUSTOM_WALLET_OPTIONS,
} from 'src/constants/wallets';
import { useLinkWallet } from './useLinkWallet';
import { Provider } from '@reown/appkit-adapter-solana/react';
import useSyncedRef from '../utils/useSyncedRef';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

interface AppKitWalletOptions {
	provider: Provider;
	address: string;
}

interface PrivyNativeWalletOptions {
	embeddedWallet: ConnectedStandardSolanaWallet;
}

const useConnectCustomWallet = () => {
	const connection = useDriftStore((s) => s.connection.current);

	const set = useDriftStore((s) => s.set);
	const { embeddedWallet } = usePrivyWalletContext();

	const { linkWalletToDriftClient } = useLinkWallet();
	const linkWalletToDriftClientRef = useSyncedRef(linkWalletToDriftClient); // using a ref to avoid race conditions with the linkWalletToDriftClient callback updates

	const connectToMagicAuth = useCallback(
		async (loginOptions: MagicAuthLoginOptions) => {
			const endpoint = connection?.rpcEndpoint as string;

			const magicWallet = createMagicWalletAdapter(endpoint);

			// disconnect and error handlers are added in useKeepWalletsInSync
			// Remove any existing 'connect' listener to avoid duplicates
			magicWallet.removeListener('connect', linkWalletToDriftClient);
			magicWallet.on('connect', linkWalletToDriftClient);

			set((state) => {
				// Clean up previous adapter's 'connect' listener if it exists
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToDriftClient
					);
				}
				state.wallet.current.adapter = magicWallet;
				state.wallet.isMagicAuth = true;
				state.wallet.isAppKit = false;
				state.wallet.isPrivyNativeAuth = false;
			});

			return await magicWallet.connect(loginOptions);
		},
		[connection, set, linkWalletToDriftClient]
	);

	const connectToAppKitWallet = useCallback(
		async (appKitOptions: AppKitWalletOptions) => {
			const appKitWallet = new AppKitAdapter(
				appKitOptions.address,
				appKitOptions.provider
			);
			appKitWallet.connected = true;
			appKitWallet.readyState = WalletReadyState.Installed;

			set((state) => {
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToDriftClient
					);
				}
				state.wallet.current.adapter = appKitWallet;
				state.wallet.isMagicAuth = false;
				state.wallet.isAppKit = true;
				state.wallet.isPrivyNativeAuth = false;
			});

			await linkWalletToDriftClientRef.current();
		},
		[set, linkWalletToDriftClient]
	);

	const connectToPrivyNative = useCallback(
		async (privyWalletOptions?: PrivyNativeWalletOptions) => {
			const currentEmbeddedWallet =
				privyWalletOptions?.embeddedWallet ?? embeddedWallet;

			if (!currentEmbeddedWallet) {
				throw new Error('No Privy embedded wallet found');
			}

			const privyNativeWallet = createPrivyNativeWalletAdapter(
				currentEmbeddedWallet
			);

			privyNativeWallet.removeListener('connect', linkWalletToDriftClient);
			privyNativeWallet.on('connect', linkWalletToDriftClient);

			set((state) => {
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToDriftClient
					);
				}
				state.wallet.current.adapter = privyNativeWallet;
				state.wallet.isMagicAuth = false;
				state.wallet.isAppKit = false;
				state.wallet.isPrivyNativeAuth = true;
			});

			await privyNativeWallet.connect();
		},
		[embeddedWallet, linkWalletToDriftClient, set]
	);

	const connectWithCustomWallet = useCallback(
		async (
			customWalletName: DriftCustomWalletName,
			opts?:
				| AppKitWalletOptions
				| MagicAuthLoginOptions
				| PrivyNativeWalletOptions
		) => {
			switch (customWalletName) {
				case DRIFT_CUSTOM_WALLET_OPTIONS.APPKIT_WALLET_ADAPTER_NAME:
					return connectToAppKitWallet(opts as AppKitWalletOptions);
				case DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME:
					return connectToMagicAuth(opts as MagicAuthLoginOptions);
				case DRIFT_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME:
					return connectToPrivyNative(opts as PrivyNativeWalletOptions);
				default: {
					throw new Error(`Unhandled custom wallet name: ${customWalletName}`);
				}
			}
		},
		[connectToAppKitWallet, connectToMagicAuth, connectToPrivyNative]
	);

	return {
		connectWithCustomWallet,
	};
};

export default useConnectCustomWallet;
